import { useState, useEffect } from 'react';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from '../contexts/AuthContext';

export interface MedicalReminderPreferences {
  id?: string;
  user_id: string;
  appointment_reminders_enabled: boolean;
  vaccination_reminders_enabled: boolean;
  birthday_reminders_enabled: boolean;
  reminder_days_before: number[];
  created_at?: string;
  updated_at?: string;
}

export const useMedicalReminderPreferences = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<MedicalReminderPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    fetchPreferences();
  }, [user]);

  const fetchPreferences = async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('user_medical_reminder_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
        throw error;
      }

      if (data) {
        setPreferences(data);
      } else {
        // Create default preferences if none exist
        const defaultPrefs: Omit<MedicalReminderPreferences, 'id' | 'created_at' | 'updated_at'> = {
          user_id: user.id,
          appointment_reminders_enabled: true,
          vaccination_reminders_enabled: true,
          birthday_reminders_enabled: true,
          reminder_days_before: [1, 3, 7]
        };
        setPreferences(defaultPrefs);
      }
    } catch (err: any) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const updatePreferences = async (newPreferences: Partial<MedicalReminderPreferences>) => {
    if (!user || !preferences) return;

    try {
      const updatedPrefs = { ...preferences, ...newPreferences };

      // Try to update first if we have an ID, otherwise insert
      if (preferences.id) {
        const { data, error } = await supabase
          .from('user_medical_reminder_preferences')
          .update({
            appointment_reminders_enabled: updatedPrefs.appointment_reminders_enabled,
            vaccination_reminders_enabled: updatedPrefs.vaccination_reminders_enabled,
            birthday_reminders_enabled: updatedPrefs.birthday_reminders_enabled,
            reminder_days_before: updatedPrefs.reminder_days_before
          })
          .eq('id', preferences.id)
          .select()
          .single();

        if (error) throw error;
        setPreferences(data);
      } else {
        // Insert new record
        const { data, error } = await supabase
          .from('user_medical_reminder_preferences')
          .insert({
            user_id: user.id,
            appointment_reminders_enabled: updatedPrefs.appointment_reminders_enabled,
            vaccination_reminders_enabled: updatedPrefs.vaccination_reminders_enabled,
            birthday_reminders_enabled: updatedPrefs.birthday_reminders_enabled,
            reminder_days_before: updatedPrefs.reminder_days_before
          })
          .select()
          .single();

        if (error) throw error;
        setPreferences(data);
      }

      return { success: true };
    } catch (err: any) {
      setError(err);
      return { success: false, error: err.message };
    }
  };

  return {
    preferences,
    isLoading,
    error,
    updatePreferences,
    refetch: fetchPreferences
  };
};